import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import API from './api';
import Login from './pages/Login';
import Signup from './pages/Signup';
import Dashboard from './pages/Dashboard';
import Clips from './pages/Clips';
import Calendar from './pages/Calendar';
import Insights from './pages/Insights';
import Admin from './pages/Admin';
import Billing from './pages/Billing';
import Account from './pages/Account';
import Onboarding from './pages/Onboarding';
import Upload from './pages/Upload';
import Layout from './components/Layout';
import './styles.css';

// Gate: must be logged in
function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

// Gate: must be logged in AND have active billing
function RequireBilling({ children }) {
  const { user, loading } = useAuth();
  const [billingStatus, setBillingStatus] = React.useState(() => {
    // Restore from localStorage to avoid redirect flash on refresh
    const cached = localStorage.getItem('clump_billing');
    return cached ? JSON.parse(cached) : null;
  });
  const [billingLoading, setBillingLoading] = React.useState(!localStorage.getItem('clump_billing'));

  React.useEffect(() => {
    if (!user) { setBillingLoading(false); return; }
    API.get('/api/billing/status')
      .then(r => {
        setBillingStatus(r.data);
        setBillingLoading(false);
        localStorage.setItem('clump_billing', JSON.stringify(r.data));
      })
      .catch(() => setBillingLoading(false));
  }, [user]);

  if (loading || billingLoading) return <div className="loading">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;

  // Admins always bypass billing gate
  if (user?.role === 'admin') return children;

  const isActive = ['active', 'trialing'].includes(billingStatus?.status);
  if (!isActive) return <Navigate to="/billing" replace />;

  return children;
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading">Loading...</div>;
  return user?.role === 'admin' ? children : <Navigate to="/dashboard" replace />;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public */}
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />

          {/* Billing — requires login but NOT active billing */}
          <Route path="/billing" element={<RequireAuth><Billing /></RequireAuth>} />

          {/* App — requires login AND active billing */}
          <Route path="/" element={<RequireBilling><Layout /></RequireBilling>}>
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="clips" element={<Clips />} />
            <Route path="calendar" element={<Calendar />} />
            <Route path="insights" element={<Insights />} />
            <Route path="admin" element={<AdminRoute><Admin /></AdminRoute>} />
            <Route path="account" element={<Account />} />
            <Route path="onboarding" element={<Onboarding />} />
            <Route path="upload" element={<Upload />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
